import { useEffect, useState } from 'react';
import { api } from '../lib/api';

type Account = {
  _id?: string;
  stripeCustomerId?: string;
  stripePaymentMethodId?: string;
  setupPaid: boolean;
  setupPaidAt?: string | null;
  subscriptionId?: string | null;
  subscriptionStatus?: string | null;
  currentSeatCount: number;
  pricePerSeat: number;
  setupFee: number;
};

type Invoice = {
  _id: string;
  stripeInvoiceId: string;
  amount: number;
  status: string;
  hostedInvoiceUrl?: string;
  createdAt: string;
};

function dollars(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function Billing() {
  const [account, setAccount] = useState<Account | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [liveSeatCount, setLiveSeatCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function load() {
    try {
      const { data } = await api.get('/api/billing/me');
      setAccount(data.account);
      setInvoices(data.invoices || []);
      setLiveSeatCount(data.liveSeatCount || 0);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load billing');
    }
  }

  useEffect(() => { load(); }, []);

  async function startSetup() {
    setBusy(true); setError(null);
    try {
      // For sprint 5: a stub flow. Real Stripe Elements integration would go here;
      // we simulate by calling setup-intent then attach with a placeholder pm.
      await api.post('/api/billing/setup-intent', {});
      setInfo('SetupIntent created. In production you would complete card details via Stripe Elements next.');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start setup');
    } finally { setBusy(false); }
  }

  async function payFee() {
    setBusy(true); setError(null);
    try {
      await api.post('/api/billing/pay-setup-fee', {});
      await load();
      setInfo('Setup fee charged.');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg || 'Charge failed');
    } finally { setBusy(false); }
  }

  async function subscribe() {
    setBusy(true); setError(null);
    try {
      await api.post('/api/billing/subscribe', {});
      await load();
      setInfo('Subscription active.');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg || 'Subscribe failed');
    } finally { setBusy(false); }
  }

  async function portal() {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post('/api/billing/portal-session', {
        returnUrl: window.location.href,
      });
      window.location.href = data.url;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Portal failed');
      setBusy(false);
    }
  }

  async function syncSeats() {
    if (!account) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/billing/update-seats', { newSeatCount: liveSeatCount });
      await load();
      setInfo('Seats synced.');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg || 'Sync failed');
    } finally { setBusy(false); }
  }

  const seatsOutOfSync = account && account.currentSeatCount !== liveSeatCount;

  return (
    <main className="text-white">
      <section className="max-w-3xl mx-auto px-8 py-10 grid gap-6">
        <h1 className="h-display text-3xl">Billing</h1>
        {error && <div className="text-red-400 text-sm">{error}</div>}
        {info && <div className="text-dgrp-red text-sm">{info}</div>}

        {!account || !account.stripeCustomerId ? (
          <div className="bg-dgal-navy/40 border border-dgrp-red/20 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-2">Get started</h2>
            <p className="text-dgal-mist text-sm mb-4">
              DG Agent Launch is $500 one-time setup + $150/mo per active agent seat.
            </p>
            <button
              onClick={startSetup}
              disabled={busy}
              className="px-4 py-2 rounded bg-dgrp-red text-white font-semibold hover:bg-dgrp-red-hover disabled:opacity-50"
            >
              {busy ? '…' : 'Start setup'}
            </button>
          </div>
        ) : !account.setupPaid ? (
          <div className="bg-dgal-navy/40 border border-dgrp-red/20 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-2">Pay the {dollars(account.setupFee)} setup fee</h2>
            <p className="text-dgal-mist text-sm mb-4">
              Once paid you can subscribe at {dollars(account.pricePerSeat)}/seat/mo.
            </p>
            <button
              onClick={payFee}
              disabled={busy || !account.stripePaymentMethodId}
              className="px-4 py-2 rounded bg-dgrp-red text-white font-semibold hover:bg-dgrp-red-hover disabled:opacity-50"
            >
              {busy ? '…' : `Pay ${dollars(account.setupFee)}`}
            </button>
            {!account.stripePaymentMethodId && (
              <div className="text-xs text-dgal-mist mt-2">Attach a payment method first via the Stripe portal.</div>
            )}
          </div>
        ) : !account.subscriptionId ? (
          <div className="bg-dgal-navy/40 border border-dgrp-red/20 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-2">Activate subscription</h2>
            <p className="text-dgal-mist text-sm mb-4">
              {liveSeatCount} active seat{liveSeatCount === 1 ? '' : 's'} × {dollars(account.pricePerSeat)} ={' '}
              <span className="text-dg-text-2">{dollars(account.pricePerSeat * Math.max(liveSeatCount, 1))}</span>/mo
            </p>
            <button
              onClick={subscribe}
              disabled={busy}
              className="px-4 py-2 rounded bg-dgrp-red text-white font-semibold hover:bg-dgrp-red-hover disabled:opacity-50"
            >
              {busy ? '…' : 'Subscribe'}
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            <div className="bg-dgal-navy/40 border border-dgrp-red/20 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold">Subscription</h2>
                <span className="text-xs text-dg-text-2 uppercase tracking-wider">{account.subscriptionStatus}</span>
              </div>
              <div className="text-sm text-dgal-mist">
                {account.currentSeatCount} seat{account.currentSeatCount === 1 ? '' : 's'} ·{' '}
                {dollars(account.pricePerSeat)}/seat/mo
              </div>
              {seatsOutOfSync && (
                <div className="mt-3 p-3 bg-yellow-700/20 border border-yellow-700/40 rounded text-xs text-yellow-200">
                  Live seat count is {liveSeatCount} but billed for {account.currentSeatCount}.{' '}
                  <button onClick={syncSeats} className="underline">Sync now</button>
                </div>
              )}
            </div>

            <button
              onClick={portal}
              disabled={busy}
              className="self-start px-4 py-2 rounded bg-dgal-navy-2 border border-dgrp-red/40 hover:border-dgrp-red"
            >
              Manage payment method (Stripe portal)
            </button>
          </div>
        )}

        <div className="bg-dgal-navy/40 border border-dgrp-red/20 rounded-lg p-6">
          <h2 className="text-sm uppercase tracking-wider text-dgal-mist mb-3">Recent invoices</h2>
          {invoices.length === 0 ? (
            <div className="text-dgal-mist text-sm">No invoices yet.</div>
          ) : (
            <ul className="divide-y divide-dgal-navy-2">
              {invoices.map((i) => (
                <li key={i._id} className="py-2 flex items-center justify-between text-sm">
                  <span>{new Date(i.createdAt).toLocaleDateString()}</span>
                  <span>{dollars(i.amount)}</span>
                  <span className="text-dgal-mist uppercase tracking-wider text-xs">{i.status}</span>
                  {i.hostedInvoiceUrl && (
                    <a href={i.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" className="text-dg-text-2 hover:text-dgrp-red">
                      View
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </main>
  );
}
